export const DEFAULT_AMOUNT = '-';
export const DEFAULT_RATE = '-';

export function number_value(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function normalize_text(value = '') {
  return String(value || '').trim();
}

export function format_hours(value = 0) {
  return `${Number(value.toFixed(1))}h`;
}

export function initials(value = '') {
  return normalize_text(value)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'TS';
}

export function journal_user_name(journal = {}) {
  return normalize_text([journal.user_first_name, journal.user_last_name].filter(Boolean).join(' ')) || 'Unknown handler';
}

export function journal_role(journal = {}) {
  return normalize_text(journal.role || journal.role_name || journal.user_role || 'HANDLER').toUpperCase();
}

export function journal_activity(journal = {}) {
  return normalize_text(
    String(journal.journal_description || '')
      .replace(/^(not billable|billable)\s*[-:]\s*/i, '')
      .replace(/\s*\|\s*submit for review\s*:\s*.+$/i, '')
  ) || 'Time journal entry';
}

export function journal_is_billable(journal = {}) {
  return !/^not billable\s*[-:]/i.test(normalize_text(journal.journal_description));
}

export function journal_hours(journal = {}) {
  const display = normalize_text(journal.hours_spent_display);
  return display.includes(':')
    ? parsed_display_hours(display)
    : numeric_hours(fallback_hours(journal));
}

export function journal_rate_value(journal = {}) {
  const rate = normalize_text(journal.hourly_rate || journal.rate || journal.user_rate || '');
  const parsed = Number(String(rate).replace(/[^\d.]/g, ''));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function format_rate(value = 0) {
  const rate = Number(value || 0);
  return rate > 0 ? `${format_currency(rate)}/hr` : DEFAULT_RATE;
}

function parsed_display_hours(display = '') {
  const [hours, minutes] = display.split(':').map(Number);
  return numeric_hours(hours) + numeric_hours(minutes) / 60;
}

function fallback_hours(journal = {}) {
  return journal.hours_spent || journal.hours || 0;
}

function numeric_hours(value) {
  return Number(value) || 0;
}

export function journal_amount(journal = {}, billable = false) {
  const amount = normalize_text(journal.amount || journal.total_amount || journal.invoice_amount);
  if (amount) return amount;
  const rate = journal_rate_value(journal);
  const hours = journal_hours(journal);
  if (billable && rate > 0 && hours > 0) {
    return format_currency(rate * hours);
  }
  return default_amount_for_billable(billable);
}

function default_amount_for_billable(billable = false) {
  if (billable) return DEFAULT_AMOUNT;
  return DEFAULT_AMOUNT;
}

export function journal_date(journal = {}) {
  return normalize_text(journal.journal_date || journal.date_created || new Date().toISOString().slice(0, 10));
}

export function week_start(value = '') {
  const date = safe_date(value);
  const day = date.getDay() || 7;
  date.setDate(date.getDate() - day + 1);
  return date.toISOString().slice(0, 10);
}

export function safe_date(value = '') {
  const plain_date_match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (plain_date_match) {
    const [, year, month, day] = plain_date_match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? new Date() : date;
}

export function week_label(value = '') {
  return `Week of ${period_label(value)}`;
}

export function month_key(value = '') {
  const date = safe_date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

export function month_label(value = '') {
  return safe_date(value).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
}

export function period_label(value = '') {
  const start = safe_date(value);
  const end = safe_date(value);
  end.setDate(start.getDate() + 6);
  return `${format_day_month(start)}-${format_day_month(end)}`;
}

export function submitted_meta(entries = []) {
  const latest = entries
    .map((entry) => safe_date(entry.submitted_at).getTime())
    .sort((left, right) => right - left)[0];
  return latest ? `Submitted ${new Date(latest).toLocaleDateString('en-ZA')}` : 'Submitted';
}

export function sum_hours(entries = []) {
  return entries.reduce((sum, entry) => sum + Number(entry.hours_value || 0), 0);
}

function format_day_month(date) {
  return date.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' });
}

function format_currency(value = 0) {
  const [whole, cents] = Number(value || 0).toFixed(2).split('.');
  return `R${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${cents}`;
}
